import React, { Component } from 'react';
import { Shield, FileSearch, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export const ToxicityAuditSection = () => {
  return <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden" data-id="element-2992" data-mp="3p0x8piav71wd">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50 pointer-events-none" data-id="element-2993" data-mp="3tsx8naqxpusy" />

      {/* Scanning Line Animation */}
      <motion.div animate={{
      y: ['0%', '100%'],
      opacity: [0, 1, 1, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent shadow-[0_0_20px_#FF0000]" data-id="element-2994" data-mp="cjs6va4ozrcx" />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10" data-id="element-2995" data-mp="2id09yutanwij">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-20" data-id="element-2996" data-mp="300bx3l0lsjgv">
          <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD700]/10 border border-[#FFD700]/30 px-4 py-2 rounded-full" data-id="element-2997" data-mp="3ixd5zgxa33q3">
            <Shield size={16} className="text-[#FFD700]" data-id="element-2998" data-mp="38zp7cfwjxc2n" />
            <span className="text-[#FFD700] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-2999" data-mp="38y4cep6brr49">
              Diagnostic Protocol
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none px-4" data-id="element-3000" data-mp="1xksgr1kpyet6">
            The <span className="text-[#FFD700]" data-id="element-3001" data-mp="1sk4ibuu5uvox">Toxicity Audit</span>
          </h2>

          <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-mono mb-4 px-4" data-id="element-3002" data-mp="kmi5v4a4g9qk">
            This isn't a "fun quiz." It's a diagnostic tool for survival.
          </p>

          <div className="inline-flex items-center gap-2 text-sm text-gray-500 font-mono" data-id="element-3003" data-mp="anas4fzh6d2x">
            <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-3004" data-mp="flm692wsz9qy" />
            <span data-id="element-3005" data-mp="xrcdj726zca">CLINICAL-GRADE ANALYSIS</span>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16" data-id="element-3006" data-mp="5pj3pvy1h93h">
          {/* Left: What It Is */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-3007" data-mp="3o4jsijk8zp0s">
            <div className="relative bg-[#050505] border-2 border-[#FFD700]/30 p-6 md:p-8 h-full" data-id="element-3008" data-mp="2zjwzpfta4oux">
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FFD700]" data-id="element-3009" data-mp="39gjilz21p5ub" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FFD700]" data-id="element-3010" data-mp="34gdzx8dr6tod" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FFD700]" data-id="element-3011" data-mp="3ig6iuhj1g5ls" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FFD700]" data-id="element-3012" data-mp="3efwh5h2a41cq" />

              <div className="flex items-center gap-3 mb-8" data-id="element-3013" data-mp="3s5gda41cu0d2">
                <div className="w-14 h-14 bg-[#FFD700] flex items-center justify-center flex-shrink-0" data-id="element-3014" data-mp="3n7etti00nz12">
                  <FileSearch size={28} className="text-black" data-id="element-3015" data-mp="5z1o0tba9vbu" />
                </div>
                <div className="min-w-0" data-id="element-3016" data-mp="5wx81d0415v7">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-3017" data-mp="119l7493oosv">
                    System Function
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-3018" data-mp="1zi0l7gm69lqf">
                    What It Does
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-3019" data-mp="297twegiejyu5">
                <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-3020" data-mp="296s9nes0xnwl">
                  The Toxicity Audit uses forensic AI to analyze conversation
                  patterns for{' '}
                  <span className="text-white font-bold" data-id="element-3021" data-mp="1gk7hwd7mz9si">
                    manipulation tactics
                  </span>{' '}
                  your brain has been trained to ignore.
                </p>

                <div className="space-y-4" data-id="element-3022" data-mp="1qi3qoyd3psng">
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3024" data-mp="2wy7dxd3d2fah" />} text="Detects gaslighting, DARVO, and covert narcissism" data-id="element-3023" data-mp="1qezh1lgva4pe" />
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3026" data-mp="q843ahrb6xcf" />} text="Maps emotional drainage patterns over time" data-id="element-3025" data-mp="q95ohf21h07v" />
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3028" data-mp="giau1nen2ubc" />} text="Identifies intermittent reinforcement cycles" data-id="element-3027" data-mp="gjcc4bu2oh2k" />
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3030" data-mp="6lxxmhrpm2cw" />} text="Generates evidence-based severity scores" data-id="element-3029" data-mp="6kwowjy61ryc" />
                </div>

                <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 p-4 rounded" data-id="element-3031" data-mp="3tp2t78hflfyt">
                  <div className="text-xs font-mono text-[#FFD700] mb-2 uppercase tracking-widest" data-id="element-3032" data-mp="3orb2uiwfq1wq">
                    ⚡ Critical Function
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-mono" data-id="element-3033" data-mp="1rw55ts6fbfl9">
                    This tool sees what you can't:{' '}
                    <span className="text-white font-bold" data-id="element-3034" data-mp="3hfd2hk1zhbi4">
                      the systematic patterns
                    </span>{' '}
                    hidden beneath "normal" conflicts.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Why You Need It */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-3035" data-mp="3ch1wjket7l56">
            <div className="relative bg-[#050505] border-2 border-[#FF0000]/30 p-6 md:p-8 h-full" data-id="element-3036" data-mp="3v56si5p861tf">
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FF0000]" data-id="element-3037" data-mp="2jrusy70i05d2" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FF0000]" data-id="element-3038" data-mp="11tzlg5y7e40w" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FF0000]" data-id="element-3039" data-mp="wveuyy8r6ypn" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FF0000]" data-id="element-3040" data-mp="1avfyklgekk8t" />

              <div className="flex items-center gap-3 mb-8" data-id="element-3041" data-mp="16t2stx1nomc1">
                <div className="w-14 h-14 bg-[#FF0000] flex items-center justify-center flex-shrink-0" data-id="element-3042" data-mp="16rhya7fcd3e6">
                  <AlertCircle size={28} className="text-white" data-id="element-3043" data-mp="j1dy6a1d3lrx" />
                </div>
                <div className="min-w-0" data-id="element-3044" data-mp="e2kicaylriol">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-3045" data-mp="rwbr3h4ieyj9">
                    Critical Need
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-3046" data-mp="n38xj8zqfp32">
                    Why It Matters
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-3047" data-mp="3rhquo01s9bkd">
                <div data-id="element-3048" data-mp="k9qa6hdzzh5j">
                  <h4 className="text-base md:text-lg font-bold text-[#FF0000] mb-4 uppercase tracking-wide" data-id="element-3049" data-mp="ka9olo4fvnet">
                    Without External Analysis:
                  </h4>
                  <ul className="space-y-3" data-id="element-3050" data-mp="t9d82t7iq4tu">
                    <ThreatPoint text="You remain trapped in cognitive dissonance" data-id="element-3051" data-mp="tbr5h8po7t4k" />
                    <ThreatPoint text="Gaslighting continues to erode your reality" data-id="element-3052" data-mp="ya0zvh3kmpp4" />
                    <ThreatPoint text="You blame yourself for 'not trying hard enough'" data-id="element-3053" data-mp="13713llcy7gd2" />
                    <ThreatPoint text="The emotional parasite feeds undetected" data-id="element-3054" data-mp="185m8hy35ao7u" />
                  </ul>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-3055" data-mp="1cyfaie78big8" />

                <div data-id="element-3056" data-mp="1h1bofrltqqdu">
                  <h4 className="text-base md:text-lg font-bold text-[#FFD700] mb-4 uppercase tracking-wide" data-id="element-3057" data-mp="1lybx8aa2678n">
                    With The Audit:
                  </h4>
                  <ul className="space-y-3" data-id="element-3058" data-mp="1qxofsitwcs5f">
                    <SolutionPoint text="Objective data validates your experience" data-id="element-3059" data-mp="211v4vmeiby2h" />
                    <SolutionPoint text="Pattern recognition breaks denial" data-id="element-3060" data-mp="1w28tzjm5itwu" />
                    <SolutionPoint text="Evidence empowers informed decisions" data-id="element-3061" data-mp="1w4bs6qxgjtcn" />
                    <SolutionPoint text="Reality anchoring prevents further manipulation" data-id="element-3062" data-mp="1r3nyfxhugyeq" />
                  </ul>
                </div>

                <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 rounded" data-id="element-3063" data-mp="2jrtl4psgkxh7">
                  <div className="text-xs font-mono text-[#FF0000] mb-2 uppercase tracking-widest flex items-center gap-2" data-id="element-3064" data-mp="2jtnx3elr5f95">
                    <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse flex-shrink-0" data-id="element-3065" data-mp="2a1i5w73br1v0" />
                    Survival Imperative
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-mono" data-id="element-3066" data-mp="25yc173vv1iqn">
                    You can't fix what you can't see. This audit makes the
                    invisible visible.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="relative" data-id="element-3067" data-mp="1n4liutd6hm1m">
          <div className="bg-gradient-to-r from-[#050505] via-[#0a0a0a] to-[#050505] border-2 border-[#FFD700] p-6 md:p-8 lg:p-12 text-center rounded" data-id="element-3068" data-mp="263fx818qckh9">
            {/* Scanning Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-3069" data-mp="2fszfb4omcab2" />

            <div className="max-w-3xl mx-auto" data-id="element-3070" data-mp="2tqe9sntqb89w">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight leading-tight" data-id="element-3071" data-mp="2ou71iodv1kos">
                Stop Guessing.
                <br data-id="element-3072" data-mp="38dculzy2z4fn" />
                <span className="text-[#FFD700]" data-id="element-3073" data-mp="3detx1zw7qyyc">Start Knowing.</span>
              </h3>

              <p className="text-gray-300 text-base md:text-lg mb-8 leading-relaxed px-4" data-id="element-3074" data-mp="2yh9wr6a9hryi">
                The Toxicity Audit isn't entertainment—it's a diagnostic
                protocol designed to detect patterns your nervous system has
                learned to ignore.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8" data-id="element-3075" data-mp="2uffz76vd542k">
                <a href="/signup" className="group relative px-8 py-4 bg-[#FFD700] text-black font-bold text-base md:text-lg uppercase tracking-wider overflow-hidden hover:bg-[#FFD700]/90 transition-all shadow-[0_0_20px_rgba(255,215,0,0.4)] hover:shadow-[0_0_30px_rgba(255,215,0,0.6)] w-full sm:w-auto min-w-[280px] rounded" data-id="element-3076" data-mp="2fqjvtn494f8a">
                  <span className="relative z-10 inline-flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200" data-id="element-3077" data-mp="2atjkawomuk9s">
                    Run Diagnostic Scan
                    <ArrowRight size={20} data-id="element-3078" data-mp="cfwxu8ax1vfk" />
                  </span>
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-gray-500 font-mono" data-id="element-3079" data-mp="7gkcxt3luyfm">
                <div className="flex items-center gap-2" data-id="element-3080" data-mp="107c3ob1wiuvk">
                  <Shield size={14} className="text-[#FFD700]" data-id="element-3081" data-mp="106sx1qhpbswu" />
                  <span data-id="element-3082" data-mp="v9j0lqvwagzi">ENCRYPTED</span>
                </div>
                <div className="w-px h-3 bg-gray-800 hidden sm:block" data-id="element-3083" data-mp="q9eixxj93g7p" />
                <div className="flex items-center gap-2" data-id="element-3084" data-mp="q7lbig0tcryn">
                  <CheckCircle size={14} className="text-[#FFD700]" data-id="element-3085" data-mp="m8ksawefbnb4" />
                  <span data-id="element-3086" data-mp="3aznfpr9ltqef">ANONYMOUS</span>
                </div>
                <div className="w-px h-3 bg-gray-800 hidden sm:block" data-id="element-3087" data-mp="3b16sv8zyrlbz" />
                <div className="flex items-center gap-2" data-id="element-3088" data-mp="36wz4xe1by9et">
                  <FileSearch size={14} className="text-[#FFD700]" data-id="element-3089" data-mp="1mfzpxmd6zt4r" />
                  <span data-id="element-3090" data-mp="1mhubn2wafp9w">EVIDENCE-BASED</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
// Helper Components
const AuditFeature = ({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) => <div className="flex items-start gap-3" data-id="element-3091" data-mp="20865p7cfny3o">
    <div className="flex-shrink-0 mt-0.5" data-id="element-3092" data-mp="2098xdhhky4kn">{icon}</div>
    <span className="text-gray-300 text-sm leading-relaxed" data-id="element-3093" data-mp="255pis3xea4u1">{text}</span>
  </div>;
const ThreatPoint = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-3094" data-mp="2a7omjex1r3ri">
    <span className="flex-shrink-0 w-6 h-6 bg-[#FF0000]/20 border border-[#FF0000]/50 flex items-center justify-center text-[#FF0000] font-bold text-xs" data-id="element-3095" data-mp="2e6xgajs371n2">
      ✕
    </span>
    <span className="text-gray-400 text-sm leading-relaxed pt-0.5" data-id="element-3096" data-mp="jt6011f95jin">{text}</span>
  </li>;
const SolutionPoint = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-3097" data-mp="ot0oyzfgvz12">
    <span className="flex-shrink-0 w-6 h-6 bg-[#FFD700]/20 border border-[#FFD700]/50 flex items-center justify-center text-[#FFD700] font-bold text-xs" data-id="element-3098" data-mp="ou2bq15ui9ym">
      ✓
    </span>
    <span className="text-gray-300 text-sm leading-relaxed pt-0.5" data-id="element-3099" data-mp="3knuar9jtiu7u">{text}</span>
  </li>;